import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type { PickerValidDate } from '@mui/x-date-pickers/models';
import type React from 'react';
import { useMemo, useRef, useState } from 'react';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';
import {
  getMetaDatePresetLabel,
  META_DATE_PRESET_OPTIONS,
  type MetaDatePreset,
  metaDatePresetToRange,
} from 'utils/metaDatePresets';

import { BASE_CALENDAR_SX } from './calendarSx';

interface MetaPeriodPickerProps {
  value: MetaDatePreset;
  onChange: (preset: MetaDatePreset) => void;
}

// Calendars are decorative — Meta doesn't accept custom ranges — so we dim them and
// block pointer events on top of the shared calendar styling.
const CALENDAR_SX = { ...BASE_CALENDAR_SX, pointerEvents: 'none', opacity: 0.55 } as const;

const MetaPeriodPicker: React.FC<MetaPeriodPickerProps> = ({ value, onChange }) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const { start, end } = useMemo(() => metaDatePresetToRange(value), [value]);
  const leftMonth = start.clone().startOf('month');
  const rightMonth = leftMonth.clone().add(1, 'month');

  const handlePresetClick = (preset: MetaDatePreset) => {
    onChange(preset);
    setOpen(false);
  };

  return (
    <>
      <Button
        ref={anchorRef}
        onClick={() => setOpen(true)}
        endIcon={<CalendarTodayIcon sx={{ fontSize: '1rem !important', color: `${NAVY}60` }} />}
        sx={{
          height: 40,
          borderRadius: RADIUS_ITEM,
          backgroundColor: `${NAVY}0A`,
          color: NAVY,
          textTransform: 'none',
          fontFamily: 'Jost, sans-serif',
          fontWeight: 500,
          fontSize: '0.875rem',
          px: 2,
          whiteSpace: 'nowrap',
          border: `1px solid rgba(21, 24, 58, 0.12)`,
          '&:hover': {
            backgroundColor: `${NAVY}14`,
            border: `1px solid rgba(21, 24, 58, 0.12)`,
          },
        }}
      >
        {getMetaDatePresetLabel(value)}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: RADIUS_CARD,
              backgroundColor: GLASS_BACKGROUND,
              backdropFilter: GLASS_BLUR,
              boxShadow: SHADOW,
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
          {/* Presets */}
          <Box
            sx={{
              width: { xs: '100%', sm: 200 },
              backgroundColor: `${NAVY}04`,
              py: 1.5,
              display: 'flex',
              flexDirection: { xs: 'row', sm: 'column' },
              overflowX: { xs: 'auto', sm: 'visible' },
              gap: 0.25,
              maxHeight: { sm: 440 },
              overflowY: { sm: 'auto' },
            }}
          >
            {META_DATE_PRESET_OPTIONS.map((opt) => {
              const isActive = value === opt.value;
              return (
                <Box
                  key={opt.value}
                  onClick={() => handlePresetClick(opt.value)}
                  sx={{
                    px: 2,
                    py: 0.75,
                    cursor: 'pointer',
                    borderRadius: RADIUS_ITEM,
                    mx: { xs: 0.5, sm: 1 },
                    flexShrink: 0,
                    backgroundColor: isActive ? `${NAVY}0F` : 'transparent',
                    transition: 'background-color 0.15s ease-in-out',
                    '&:hover': { backgroundColor: isActive ? `${NAVY}0F` : `${NAVY}08` },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      fontSize: '0.85rem',
                      fontWeight: isActive ? 700 : 400,
                      color: isActive ? NAVY : `${NAVY}99`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {opt.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* Calendars + warning */}
          <Box sx={{ display: 'flex', flexDirection: 'column', backgroundColor: SURFACE_CONTAINER_LOWEST }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mx: 2,
                mt: 2,
                px: 1.5,
                py: 1,
                borderRadius: RADIUS_ITEM,
                backgroundColor: alpha(YELLOW, 0.12),
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: '1.1rem', color: NAVY }} />
              <Typography
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontSize: '0.78rem',
                  fontWeight: 500,
                  color: NAVY,
                  lineHeight: 1.35,
                }}
              >
                Meta solo permite periodos fijos. Seleccioná un preset de la izquierda.
              </Typography>
            </Box>

            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Box sx={{ display: 'flex' }}>
                <DateCalendar
                  value={start as unknown as PickerValidDate}
                  referenceDate={leftMonth as unknown as PickerValidDate}
                  readOnly
                  disabled
                  sx={CALENDAR_SX}
                />
                <DateCalendar
                  value={end as unknown as PickerValidDate}
                  referenceDate={rightMonth as unknown as PickerValidDate}
                  readOnly
                  disabled
                  sx={{ ...CALENDAR_SX, display: { xs: 'none', sm: 'block' } }}
                />
              </Box>
            </LocalizationProvider>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1,
                px: 2,
                py: 1.25,
                backgroundColor: `${NAVY}04`,
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: NAVY,
                }}
              >
                {start.format('DD/MM/YY')} – {end.format('DD/MM/YY')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default MetaPeriodPicker;
